import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { useAuthRoles } from '../../../../hooks/useAuthRoles';
import {
  X,
  Home,
  Briefcase,
  Plug,
  Settings as SettingsIcon,
  CreditCard,
  Shield,
  LogOut,
} from 'lucide-react';
import { Button } from '@/components/ui/atoms/Button';
import { SidebarItem } from './SidebarItem';
import { SidebarGroup } from './SidebarGroup';
import { PlanChip } from './PlanChip';

interface MobileNavSheetProps {
  open: boolean;
  onClose: () => void;
}

export const MobileNavSheet = ({ open, onClose }: MobileNavSheetProps) => {
  const { t } = useTranslation('common');
  const { isAdmin } = useAuthRoles();
  const { user, logout } = useAuth0();
  const brandLogoSrc = '/logo/dark-logo.png';
  const brandName = 'Invirtus';

  // Lock body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="md:hidden fixed inset-0 z-50">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-dark-invirtus/40 animate-fade-in"
        onClick={onClose}
        aria-hidden="true"
      />
      {/* Sheet (slides from left, full-height) */}
      <div className="absolute inset-y-0 left-0 w-[85%] max-w-[320px] bg-light flex flex-col shadow-[var(--shadow-sheet)]">
        {/* Header */}
        <div className="flex items-center justify-between px-3 h-14 border-b border-light-lila/40">
          <Link to="/home" onClick={onClose} className="flex items-center gap-2">
            <img src={brandLogoSrc} alt="" className="h-8 w-auto max-w-16 object-contain" />
            <span className="text-dark-invirtus text-lg font-bold font-heading tracking-tight">
              {brandName}
            </span>
          </Link>
          <Button
            type="icon"
            intent="muted"
            size="sm"
            icon={<X />}
            onClick={onClose}
            aria-label={t('nav.close_nav')}
          />
        </div>

        {/* Body */}
        <nav className="flex-1 overflow-y-auto px-2 py-2">
          <SidebarGroup label={t('nav.section_primary')}>
            <SidebarItem to="/home" icon={<Home />} label={t('nav.home')} matchPaths={['/home']} onClick={onClose} />
            <SidebarItem
              to="/jobs"
              icon={<Briefcase />}
              label={t('nav.jobs')}
              matchPaths={['/dashboard', '/jobs', '/job']}
              onClick={onClose}
            />
          </SidebarGroup>

          <SidebarGroup label={t('nav.section_workspace')}>
            <SidebarItem
              to="/integrations"
              icon={<Plug />}
              label={t('nav.integrations')}
              matchPaths={['/integrations']}
              onClick={onClose}
            />
            <SidebarItem
              to="/settings"
              icon={<SettingsIcon />}
              label={t('nav.settings')}
              matchPaths={['/settings']}
              onClick={onClose}
            />
            <SidebarItem
              to="/billing"
              icon={<CreditCard />}
              label={t('nav.billing')}
              matchPaths={['/billing']}
              onClick={onClose}
            />
          </SidebarGroup>

          {isAdmin && (
            <SidebarGroup label={t('nav.section_admin')}>
              <SidebarItem
                to="/admin"
                icon={<Shield />}
                label={t('nav.admin')}
                matchPaths={['/admin']}
                onClick={onClose}
              />
            </SidebarGroup>
          )}
        </nav>

        {/* Footer */}
        <div className="border-t border-light-lila/40 px-2 py-2 space-y-2">
          <PlanChip />
          <div className="flex items-center gap-2 rounded-md p-1.5">
            {user?.picture ? (
              <img src={user.picture} alt="" className="h-8 w-8 rounded-full object-cover shrink-0" />
            ) : (
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-purple-soft/40 text-dark-invirtus shrink-0">
                <span className="text-[11px] font-bold font-heading">
                  {user?.name
                    ? user.name
                        .split(' ')
                        .map((w) => w.charAt(0))
                        .join('')
                        .toUpperCase()
                        .slice(0, 2)
                    : 'U'}
                </span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-dark-invirtus truncate font-body">
                {user?.name || 'User'}
              </div>
              <div className="text-xs text-dark-invirtus/60 truncate font-body">
                {user?.email}
              </div>
            </div>
            <Button
              type="icon"
              intent="muted"
              size="sm"
              icon={<LogOut />}
              onClick={() =>
                logout({ logoutParams: { returnTo: window.location.origin } })
              }
              aria-label={t('nav.sign_out')}
              title={t('nav.sign_out')}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
